import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getErrorMessage } from 'utils/error';
import { programsAPI, organizationAPI } from 'api';
import { ExploreJobSeekerProgramsState, JobSeekerDiscoverProgramTableView, JobSeekerProgramMembership } from './types';
import { Paging, Tab, TableTabFilters } from 'api/types';
import { RootState } from 'app/root-reducer';
import { RejectWithMessage, TableDataResponse } from 'features/types';
import initialRequestData from 'features/initial-request-data';
import { OrganizationMainFields } from 'features/organizations/types';

export const getJobSeekerPrograms = createAsyncThunk<TableDataResponse<JobSeekerDiscoverProgramTableView>, void, RejectWithMessage>(
  'job-seeker-explore-programs/get-programs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      // const requestData = { ...state.jobSeekerExplorePrograms.requestData, filters: state.programsFilters.filters };
      const requestData = state.jobSeekerExplorePrograms.requestData;
      return await programsAPI.getJobSeekerTable(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getTabs = createAsyncThunk<Tab[], void, RejectWithMessage>(
  'job-seeker-explore-programs/get-tabs',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state: RootState = getState();
      const requestData = state.jobSeekerExplorePrograms.requestData;
      return programsAPI.getJobSeekerTabsData(requestData);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getJobSeekerCBODetails = createAsyncThunk<OrganizationMainFields, string, RejectWithMessage>(
  'job-seeker-explore-programs/get-cbo-details',
  async (id, { rejectWithValue }) => {
    try {
      return await organizationAPI.getJobSeekerCBODetails(id);
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const initialState: ExploreJobSeekerProgramsState = {
  status: 'idle',
  error: null,
  requestData: {
    paging: {
      page: 1,
      pageSize: 20,
    },
    filters: { tab: JobSeekerProgramMembership.Discover },
  },
  data: {
    tabsIsLoading: false,
    tabs: [],
    tableDataIsLoading: false,
    tableData: {
      rows: [],
      totalCount: 0,
      noResult: false,
    },
    selectedOrganization: null,
  },
};

const jobSeekerExplorePrograms = createSlice({
  name: 'jobSeekerExplorePrograms',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<Paging>) => {
      state.requestData.paging.page = action.payload.page;
      state.requestData.paging.pageSize = action.payload.pageSize;
    },
    setFilters: (state, action: PayloadAction<Partial<TableTabFilters>>) => {
      state.requestData.filters = { ...state.requestData.filters, ...action.payload };
      state.requestData.paging = initialRequestData.paging;
    },
    resetRequestData: (state) => {
      state.requestData = initialState.requestData;
    },
    resetPaging: (state) => {
      state.requestData.paging = initialState.requestData.paging;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getJobSeekerPrograms.pending, (state) => {
      state.data.tableDataIsLoading = true;
    });
    builder.addCase(getJobSeekerPrograms.fulfilled, (state, action: PayloadAction<TableDataResponse<JobSeekerDiscoverProgramTableView>>) => {
      state.data.tableDataIsLoading = false;
      state.data.tableData = action.payload;
      state.data.tableData.noResult = state.data.tableData.rows.length === 0;
    });
    builder.addCase(getJobSeekerPrograms.rejected, (state) => {
      state.data.tableDataIsLoading = false;
    });

    builder.addCase(getTabs.pending, (state) => {
      state.data.tabsIsLoading = true;
    });
    builder.addCase(getTabs.fulfilled, (state, action: PayloadAction<Tab[]>) => {
      state.data.tabsIsLoading = false;
      state.data.tabs = action.payload;
    });
    builder.addCase(getTabs.rejected, (state) => {
      state.data.tabsIsLoading = false;
    });

    builder.addCase(getJobSeekerCBODetails.pending, (state) => {
      state.status = 'pending';
    });
    builder.addCase(getJobSeekerCBODetails.fulfilled, (state, action: PayloadAction<OrganizationMainFields>) => {
      state.status = 'fulfilled';
      state.data.selectedOrganization = action.payload;
      state.error = null;
    });
    builder.addCase(getJobSeekerCBODetails.rejected, (state) => {
      state.status = 'rejected';
    });
  }
});

export const { setFilters, setPage, resetRequestData, resetPaging, resetState } = jobSeekerExplorePrograms.actions;
export const jobSeekerExploreProgramsReducer = jobSeekerExplorePrograms.reducer;
